import { deg, fixed, trim } from '../../math/format';
import { taperFromDiameters, taperPerInchFromPerFoot } from '../../math/geometry';
import { mcOptions } from '../helpers';
import type { QuestionTemplate } from '../types';

export const TAPER_TEMPLATES: readonly QuestionTemplate[] = [
  {
    id: 'taper.perFootToPerInch',
    topicId: 'taper',
    skill: 'Taper per foot to per inch',
    gen: (rng) => {
      const tpf = rng.pick([0.5, 0.6, 0.75, 1.0, 1.5, 2.0, 3.0]);
      const solved = taperPerInchFromPerFoot(tpf);
      const correct = `${fixed(solved.value, 4)}"`;

      return {
        kind: 'mc',
        prompt: `Taper is ${fixed(tpf, 3)}" per foot. Per inch?`,
        formula: 'TPI = TPF ÷ 12',
        why: `${trim(tpf, 3)} ÷ 12 = ${fixed(solved.value, 4)}" per inch.`,
        steps: solved.steps,
        ...mcOptions(rng, correct, [
          {
            label: `${fixed(tpf * 12, 3)}"`,
            trap: 'You multiplied. The same taper spread over more length has to be less per inch, not more.',
          },
          {
            label: `${fixed(solved.value * 10, 4)}"`,
            trap: 'You divided by 10 somewhere. A foot is 12 inches.',
          },
          {
            label: `${fixed(tpf / 2, 4)}"`,
            trap: 'You halved it. Halving belongs to the compound angle, not to this conversion.',
          },
        ]),
      };
    },
  },
  {
    id: 'taper.fromDiameters',
    topicId: 'taper',
    skill: 'Taper from two diameters and a length',
    gen: (rng) => {
      const small = rng.step(0.25, 1.0, 0.125);
      const rise = rng.pick([0.125, 0.25, 0.375, 0.5]);
      const large = Math.round((small + rise) * 10000) / 10000;
      // A 1" length is excluded on purpose: it makes TPI equal the raw
      // diameter difference, which collapses a distractor onto the answer.
      const length = rng.pick([2.0, 3.0, 4.0]);
      const solved = taperFromDiameters(large, small, length);
      const correct = `${fixed(solved.value.tpi, 4)}"`;

      return {
        kind: 'mc',
        prompt: `Big Ø ${fixed(large, 3)}", small Ø ${fixed(small, 3)}", length ${fixed(length, 3)}". TPI?`,
        formula: 'TPI = (big Ø − small Ø) ÷ length',
        why: `(${trim(large, 3)} − ${trim(small, 3)}) ÷ ${trim(length, 3)} = ${fixed(solved.value.tpi, 4)}" per inch, which is ${fixed(solved.value.tpf, 3)}" per foot.`,
        steps: solved.steps,
        ...mcOptions(rng, correct, [
          {
            label: `${fixed(solved.value.tpi / 2, 4)}"`,
            trap: 'You halved the diameter difference. That halving belongs to the compound angle, not to taper per inch.',
          },
          {
            label: `${fixed(rise, 4)}"`,
            trap: 'That is the total difference in diameter. You still have to spread it over the length.',
          },
          {
            label: `${fixed(solved.value.tpf, 4)}"`,
            trap: 'That is per foot. The question asked per inch — divide by 12.',
          },
        ]),
      };
    },
  },
  {
    id: 'taper.compound',
    topicId: 'taper',
    skill: 'What angle the compound gets',
    gen: (rng) => {
      const small = rng.step(0.5, 1.0, 0.125);
      const rise = rng.pick([0.25, 0.375, 0.5]);
      const length = rng.pick([2.0, 3.0]);
      const solved = taperFromDiameters(
        Math.round((small + rise) * 10000) / 10000,
        small,
        length,
      );
      const { includedAngle, compoundAngle } = solved.value;

      return {
        kind: 'mc',
        prompt: `That taper has a ${deg(includedAngle)} included angle. What do you set the compound to?`,
        formula: 'compound = included angle ÷ 2',
        why: `The compound only cuts one side of the work. The two sides together make the included angle, so it gets half: ${deg(compoundAngle)}.`,
        steps: [
          { kind: 'note', text: 'The included angle is both sides of the taper added together.' },
          { kind: 'formula', text: 'compound = included ÷ 2' },
          { kind: 'compute', text: `${deg(includedAngle)} ÷ 2` },
          { kind: 'result', text: `= ${deg(compoundAngle)}` },
        ],
        ...mcOptions(rng, deg(compoundAngle), [
          {
            label: deg(includedAngle),
            trap: 'That is the full included angle, which is both sides. The compound only cuts one.',
          },
          {
            label: deg(includedAngle * 2),
            trap: 'Doubled the included angle. That is twice as wrong as using it directly.',
          },
          {
            label: deg(90 - compoundAngle),
            trap: 'That is measuring from the other reference. The compound is set from the work axis.',
          },
        ]),
      };
    },
  },
  {
    id: 'taper.lengthEffect',
    topicId: 'taper',
    skill: 'How length changes taper per inch',
    gen: (rng) => {
      const factor = rng.pick([2, 3, 4]);
      const answers: Record<number, string> = {
        2: 'Halves',
        3: 'Drops to a third',
        4: 'Drops to a quarter',
      };
      const wrongLabels: Record<number, string> = {
        2: 'Doubles',
        3: 'Triples',
        4: 'Quadruples',
      };

      return {
        kind: 'mc',
        prompt: `Same two diameters, but the taper is ${factor} times as long. TPI...`,
        formula: 'TPI = (big Ø − small Ø) ÷ length',
        why: `The same rise spread over ${factor} times the length gives one ${factor === 2 ? 'half' : factor === 3 ? 'third' : 'quarter'} the taper per inch.`,
        steps: [
          { kind: 'formula', text: 'TPI = (big Ø − small Ø) ÷ length' },
          { kind: 'note', text: 'Length is a divisor. Growing it shrinks the answer.' },
          { kind: 'result', text: `${factor}× the length gives 1/${factor} the TPI.` },
        ],
        ...mcOptions(rng, answers[factor]!, [
          {
            label: wrongLabels[factor]!,
            trap: 'Wrong direction. Spreading the same rise over more length makes it gentler, not steeper.',
          },
          { label: 'Stays the same', trap: 'Length is in the formula, so changing it has to change the answer.' },
          {
            label: 'Goes to zero',
            trap: 'It gets smaller, but it never disappears — the diameters still differ.',
          },
        ]),
      };
    },
  },
];
